import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import axios from "axios";
import { toastr } from "react-redux-toastr";
import { parse } from "flatted";

import Row from "../../../common/layout/row";
import LabelAndInput from "../../../common/form/LabelAndInput";
import LabelAndSelect from "../../../common/form/LabelAndSelect";
import Table from "../../../common/layout/Table";
import Grid from "../../../common/layout/grid";
import { getList } from "../duck/crashLog.duck";
import { getList as getListBets } from "../duck/crashBet.duck";
import { formatLocalShort, formatUtcAsLocalShort, localInputHoursFromNow } from "../utils/datetime";
import CheckMultiSelect from "./CheckMultiSelect";
import CrashDashboard from "./CrashDashboard";
import BotPromptGaleSelect, { DEFAULT_GALE_TYPE, parseBotPromptBindings } from "./BotPromptGaleSelect";

/** CrashSignal::SOURCE_* na API. */
const SOURCES = {
  0: "Manual",
  1: "Matriz Profunda",
  2: "Matriz Rasa",
  3: "Matriz Reversa",
  4: "Dinâmica",
  5: "3x",
  6: "Gerado",
};

const REFRESH_MS = 10000;

/**
 * Atividade dos bots: apostas gravadas em crash_bets e o log que o orquestrador
 * envia para crash_logs. As duas listas são por usuário bot e por janela de tempo.
 */
class CrashLog extends Component {
  constructor(props) {
    super(props);

    const selfOnly = !!props.selfOnly;
    const sessionUserId = props.sessionUserId != null ? Number(props.sessionUserId) : null;

    this.state = {
      userId: selfOnly && Number.isFinite(sessionUserId) ? sessionUserId : null,
      // Gráfico / listas: últimas 24 horas (agora − 24h → agora).
      start: localInputHoursFromNow(-24),
      end: localInputHoursFromNow(0),
      users: [],
      prompts: [],
      promptIds: [],
      galeByPrompt: {},
      logObjects: [],
      autoRefresh: true,
      savingPrompts: false,
    };
  }

  refreshTimer = null;

  get selfOnly() {
    return !!this.props.selfOnly;
  }

  componentWillMount() {
    if (this.selfOnly) {
      this.bootstrapSelfBot();
    } else {
      this.getUsers();
    }
    this.getPrompts();
  }

  componentDidMount() {
    this.refresh();
    this.refreshTimer = setInterval(() => {
      if (this.state.autoRefresh) this.refresh();
    }, REFRESH_MS);
  }

  componentDidUpdate(prevProps) {
    if (this.selfOnly && Number(prevProps.sessionUserId) !== Number(this.props.sessionUserId)) {
      this.bootstrapSelfBot();
    }
  }

  componentWillUnmount() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /** Perfil Bot: só o próprio usuário, sem listar outros. */
  bootstrapSelfBot = () => {
    const id = this.props.sessionUserId != null ? Number(this.props.sessionUserId) : null;
    if (!Number.isFinite(id)) return;
    const name = this.props.sessionUserName || `Bot #${id}`;
    const promptIds = this.parsePromptIds(this.props.sessionBotPromptIds);
    const galeByPrompt = this.galeMapFromBindings(
      parseBotPromptBindings(this.props.sessionBotPrompts || this.props.sessionBotPromptIds)
    );
    this.setState(
      {
        userId: id,
        promptIds,
        galeByPrompt,
        users: [{ id, name, bot_prompt_ids: promptIds, bot_prompts: parseBotPromptBindings(this.props.sessionBotPrompts || this.props.sessionBotPromptIds) }],
      },
      this.refresh
    );
  };

  refresh = () => {
    const { userId, start, end, promptIds } = this.state;
    this.props.getList(userId, start, end);
    // Admin: todos os bots no gráfico. Perfil Bot: só o próprio.
    const betsUserId = this.selfOnly ? userId : null;
    this.props.getListBets(betsUserId, start, end, promptIds);
  };

  /** Usuários do perfil Bot: são os únicos que geram apostas de bot. */
  getUsers = () => {
    axios
      .get(`${process.env.REACT_APP_API_HOST}/profiles?limit=100`, { silent: true })
      .then((resp) => {
        const profiles = (resp.data && resp.data.data) || [];
        const bot = profiles.find((p) => p.noun === "Bot");
        if (!bot) return;

        return axios
          .get(
            `${process.env.REACT_APP_API_HOST}/user_profiles?search=profile_id:${bot.id}&with=user:id,name,login,bot_prompt_ids&limit=200`,
            { silent: true }
          )
          .then((userResp) => {
            const rows = (userResp.data && userResp.data.data) || [];
            const users = rows
              .filter((row) => row.user && row.user.id)
              .map((row) => {
                const bindings = parseBotPromptBindings(row.user.bot_prompt_ids);
                return {
                  id: row.user.id,
                  name: row.user.name || row.user.login,
                  bot_prompt_ids: bindings.map((b) => b.crash_prompt_id),
                  bot_prompts: bindings,
                };
              });
            this.setState({ users }, () => {
              if (this.state.userId != null) {
                this.loadBotPromptBindings(this.state.userId);
              }
            });
          });
      })
      .catch((e) => {
        toastr.error("Erro", (e.response && e.response.data && e.response.data.message) || "Falha ao carregar bots");
      });
  };

  getPrompts = () => {
    axios
      .get(`${process.env.REACT_APP_API_HOST}/crash_prompts?orderBy=id&sortedBy=asc&limit=100`, { silent: true })
      .then((resp) => {
        const rows = (resp.data && resp.data.data) || [];
        this.setState({
          prompts: rows.map((p) => {
            const min = Number(p.min_result);
            const minResult = Number.isFinite(min) && min > 1 ? min : 2;
            return {
              id: p.id,
              name: p.title || `Prompt #${p.id}`,
              title: p.title,
              min_result: minResult,
              active: Number(p.active) === 1,
            };
          }),
        });
      })
      .catch(() => {
        this.setState({ prompts: [] });
      });
  };

  parsePromptIds = (raw) => {
    return parseBotPromptBindings(raw).map((b) => b.crash_prompt_id);
  };

  galeMapFromBindings = (bindings) => {
    const map = {};
    (bindings || []).forEach((b) => {
      map[b.crash_prompt_id] = b.gale_type || DEFAULT_GALE_TYPE;
    });
    return map;
  };

  applyBotBindings = (userId, bindings) => {
    const promptIds = (bindings || []).map((b) => b.crash_prompt_id);
    const galeByPrompt = this.galeMapFromBindings(bindings);
    this.setState((prev) => ({
      promptIds,
      galeByPrompt,
      users: prev.users.map((u) =>
        Number(u.id) === Number(userId)
          ? { ...u, bot_prompt_ids: promptIds, bot_prompts: bindings }
          : u
      ),
    }));
  };

  loadBotPromptBindings = (userId) => {
    if (userId == null) return;
    axios
      .get(`${process.env.REACT_APP_API_HOST}/users/${userId}`, { silent: true })
      .then((resp) => {
        const data = (resp.data && resp.data.data) || {};
        const bindings = Array.isArray(data.bot_prompts) && data.bot_prompts.length
          ? parseBotPromptBindings(data.bot_prompts)
          : parseBotPromptBindings(data.bot_prompt_ids);
        this.applyBotBindings(userId, bindings);
      })
      .catch(() => {
        const user = (this.state.users || []).find((u) => Number(u.id) === Number(userId));
        this.applyBotBindings(userId, parseBotPromptBindings(user && (user.bot_prompts || user.bot_prompt_ids)));
      });
  };

  onBotChange = (value) => {
    if (this.selfOnly) return;
    const userId = value == null || value === "" ? null : Number.parseInt(value, 10);
    if (!Number.isFinite(userId) && userId !== null) {
      this.setState({ userId: null }, this.refresh);
      return;
    }
    const user = (this.state.users || []).find((u) => Number(u.id) === Number(userId));
    const bindings = parseBotPromptBindings(user && (user.bot_prompts || user.bot_prompt_ids));
    this.setState(
      {
        userId,
        promptIds: userId == null ? this.state.promptIds : bindings.map((b) => b.crash_prompt_id),
        galeByPrompt: userId == null ? {} : this.galeMapFromBindings(bindings),
      },
      () => {
        this.refresh();
        if (userId != null) this.loadBotPromptBindings(userId);
      }
    );
  };

  /** Seleção a partir do gráfico (legenda ou ponto). */
  onSelectBotFromChart = (userId) => {
    if (this.selfOnly) return;
    const id = userId == null || userId === "" ? null : Number(userId);
    if (id != null && Number(this.state.userId) === id) return;
    this.onBotChange(id);
  };

  /**
   * Atualiza filtro e, com bot selecionado, persiste os prompts + tipo de gale.
   */
  onPromptIdsChange = (promptIds) => {
    this.setState({ promptIds }, () => {
      this.refresh();
    });
  };

  onBotPromptBindingsChange = ({ promptIds, galeByPrompt, bindings }) => {
    this.setState({ promptIds, galeByPrompt }, () => {
      this.refresh();
      if (!this.selfOnly && this.state.userId != null) {
        this.saveBotPrompts(this.state.userId, bindings);
      }
    });
  };

  saveBotPrompts = (userId, bindings) => {
    this.setState({ savingPrompts: true });
    const payload = {
      bot_prompt_ids: (bindings || []).map((b) => ({
        id: b.crash_prompt_id,
        gale_type: b.gale_type || DEFAULT_GALE_TYPE,
      })),
    };
    axios
      .put(`${process.env.REACT_APP_API_HOST}/users/${userId}`, payload)
      .then((resp) => {
        const data = (resp.data && resp.data.data) || {};
        const saved = Array.isArray(data.bot_prompts)
          ? parseBotPromptBindings(data.bot_prompts)
          : payload.bot_prompts;
        this.setState((prev) => ({
          savingPrompts: false,
          promptIds: saved.map((b) => b.crash_prompt_id),
          galeByPrompt: this.galeMapFromBindings(saved),
          users: prev.users.map((u) =>
            Number(u.id) === Number(userId)
              ? { ...u, bot_prompt_ids: saved.map((b) => b.crash_prompt_id), bot_prompts: saved }
              : u
          ),
        }));
        toastr.success("Prompts", "Bot atualizado: prompts e tipo de gale gravados (vazio = todos).");
      })
      .catch((e) => {
        this.setState({ savingPrompts: false });
        toastr.error(
          "Erro",
          (e.response && e.response.data && e.response.data.message) || "Falha ao salvar prompts do bot"
        );
      });
  };

  getCrashLogObect = (e, logId = null) => {
    if (this.state.logObjects[logId]) {
      this.expandObj(e, this.state.logObjects[logId]);
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_HOST}/crash_log_objects/${logId}`)
      .then((resp) => {
        if (!resp.data.data.id) return;

        const objs = [...this.state.logObjects];
        objs[resp.data.data.id] = {
          ...resp.data.data,
          json: resp.data.data.json ? parse(resp.data.data.json) : null,
          opened: true,
        };

        this.setState({ logObjects: objs });

        if (objs[resp.data.data.id]) {
          this.expandObj(e, objs[resp.data.data.id]);
        } else {
          toastr.warning("Ops!", "Objeto JSON inválido no log " + logId);
        }
      })
      .catch((e) => {
        if (!e.response) {
          toastr.error("Erro", "Desconhecido :-/");
          console.log(e);
        } else if (e.response.status === 404) {
          toastr.warning("Ops!", "Log sem objeto!");
        } else if (e.response.data) {
          toastr.error("Erro", e.response.data.message);
        }
      });
  };

  renderMessage = (val, item) => {
    return (
      <div>
        <span style={{ float: "left" }}>{val} </span>
        <div>
          <span style={{ cursor: "pointer" }} onClick={(e) => this.getCrashLogObect(e.target, item.id)}>
            <b>{"{ }"}</b>
          </span>
          <div id={"logObj" + item.id} />
        </div>
      </div>
    );
  };

  expandObj = (e, obj, i = 1) => {
    if (!obj || typeof obj !== "object") {
      toastr.error("Objeto inválido");
      return false;
    }

    const div =
      typeof e === "string"
        ? document.getElementById(e)
        : e.tagName === "SPAN"
        ? e.nextElementSibling
        : e.parentElement.nextElementSibling;

    if (!div) return false;

    if (div.children.length > 0) {
      div.innerHTML = "";
      return true;
    }

    Object.keys(obj).forEach((key) => {
      const span = document.createElement("span");
      span.style.paddingLeft = i + "%";
      span.style.display = "block";
      span.style.float = "left";
      span.style.width = 70 - i + "%";
      span.append(key + ": ");

      if (obj[key] && typeof obj[key] === "object") {
        const nested = document.createElement("span");
        nested.onclick = (ev) => this.expandObj(ev.target, obj[key], i + 1);
        nested.innerHTML = "<i>{ }</i>";
        nested.style.fontStyle = "italic";
        nested.style.cursor = "pointer";
        span.appendChild(nested);
        span.appendChild(document.createElement("div"));
      } else {
        span.append(obj[key]);
      }

      div.appendChild(span);
    });

    return true;
  };

  renderFilters = () => {
    const locked = this.selfOnly;
    return (
      <>
        <Row cols='12 3 3'>
          <LabelAndSelect
            label='Bot'
            name='selectUser'
            readOnly={locked}
            forceToShow={true}
            cols='12 3 3'
            isClearable={!locked}
            options={this.state.users}
            input={{
              value: this.state.userId,
              onChange: this.onBotChange,
            }}
          />

          <LabelAndInput
            label='Início'
            name='start'
            type='datetime-local'
            readOnly={false}
            forceToShow={true}
            cols='12 3 3'
            input={{
              value: this.state.start,
              onChange: (e) => this.setState({ start: e.target.value }, this.refresh),
            }}
          />

          <LabelAndInput
            label='Fim'
            name='end'
            type='datetime-local'
            readOnly={false}
            forceToShow={true}
            cols='12 3 3'
            input={{
              value: this.state.end,
              onChange: (e) => this.setState({ end: e.target.value }, this.refresh),
            }}
          />

          <LabelAndInput
            label='Auto Refresh'
            name='autoRefresh'
            type='checkbox'
            readOnly={false}
            forceToShow={true}
            cols='12 3 3'
            input={{
              checked: this.state.autoRefresh,
              onChange: (e) => this.setState({ autoRefresh: e.target.checked }),
            }}
          />
        </Row>
        <Row>
          {this.state.userId != null ? (
            <BotPromptGaleSelect
              label={
                locked
                  ? "Prompts e tipo de gale deste bot"
                  : `Prompts que o bot joga${this.state.savingPrompts ? " (salvando…)" : ""}`
              }
              cols='12 6 6'
              options={this.state.prompts}
              value={this.state.promptIds}
              galeByPrompt={this.state.galeByPrompt}
              onChange={this.onBotPromptBindingsChange}
              emptyLabel='Nenhum prompt cadastrado'
              disabled={locked}
            />
          ) : (
            <CheckMultiSelect
              label='Prompts (filtro da lista)'
              cols='12 6 6'
              options={this.state.prompts}
              value={this.state.promptIds}
              onChange={this.onPromptIdsChange}
              emptyLabel='Nenhum prompt cadastrado'
              disabled={false}
            />
          )}
          <Grid cols='12 6 6'>
            <p style={{ color: '#757575', fontSize: 12, padding: '28px 15px 0' }}>
              {locked
                ? "Você vê apenas a atividade do seu bot. Os prompts e o tipo de gale são somente leitura."
                : this.state.userId != null
                ? "Cada prompt marcado tem um tipo de gale. Martingale dobra só no sinal (esgotou o gale, a perda encerra). Soros gale leva o prejuízo ao próximo sinal (entrada + metade). O auto-retirar não muda a escada. Linear (3, 6, 9, 12) é a estratégia calculada. Lista vazia = todos os prompts, gale martingale."
                : "Com um bot selecionado, os prompts marcados e o tipo de gale são gravados nele. Sem bot, a lista só filtra a tela."}
            </p>
          </Grid>
        </Row>
      </>
    );
  };

  render() {
    const allBets = this.props.bets || [];
    const selectedId = this.state.userId != null ? Number(this.state.userId) : null;
    const filteredBets =
      selectedId == null
        ? allBets
        : allBets.filter((bet) => Number(bet.user_id) === selectedId);

    return (
      <>
        <CrashDashboard
          bets={allBets}
          selectedUserId={this.state.userId}
          onSelectBot={this.onSelectBotFromChart}
        />
        {this.renderFilters()}
        <Row>
          <Grid cols='12 6 6'>
            <Table
              renderHead={false}
              cols='12 6 6'
              title='LOGS DO BOT'
              body={this.props.logs}
              labelMobile='message'
              actions={{}}
              attributes={{
                message: { title: "LOGS DO BOT", callback: this.renderMessage },
              }}
            />
          </Grid>
          <Grid cols='12 6 6'>
            <Table
              title='APOSTAS DO BOT'
              cols='12 6 6'
              body={filteredBets}
              labelMobile='value'
              attributes={{
                crash_signal: {
                  title: "Sinal",
                  callback: (val) => {
                    if (!val || !val.id) return "—";
                    const source = SOURCES[Number(val.source) || 0];
                    const prompt = val.crash_prompt_id
                      ? (this.state.prompts.find((p) => Number(p.id) === Number(val.crash_prompt_id)) || {}).name
                      : null;
                    return (
                      <span title={`Origem: ${source}${prompt ? ` · ${prompt}` : ""}`}>
                        {formatLocalShort(val.date)}
                        {prompt ? <small style={{ display: "block", color: "#757575" }}>{prompt}</small> : null}
                      </span>
                    );
                  },
                },
                value: { title: "Entrada", callback: (val) => `R$ ${Number(val).toFixed(2)}` },
                factor: { title: "Auto-retirar", callback: (val) => `${Number(val).toFixed(2)}x` },
                crash_result: {
                  title: "Crash",
                  callback: (val) => (val ? `${Number(val.value).toFixed(2)}x` : "—"),
                },
                id: {
                  title: "Resultado em",
                  callback: (val, item) =>
                    item.crash_result ? formatUtcAsLocalShort(item.crash_result.created_at) : "—",
                },
                credit: {
                  title: "Saldo",
                  callback: (val) => `R$ ${Math.round(Number(val) || 0)}`,
                },
                win: {
                  title: "Ganhou",
                  callback: (val, item) => {
                    const won = Number(item && item.win) === 1;
                    return <b style={{ color: won ? "#2e7d32" : "#c62828" }}>{won ? "Sim" : "Não"}</b>;
                  },
                },
                user: {
                  title: "Bot",
                  callback: (val, item) => (val && val.name ? val.name : item && item.user_id ? `#${item.user_id}` : "—"),
                },
              }}
            />
          </Grid>
        </Row>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  logs: state.crashLog.list,
  bets: state.crashBet.list,
  sessionUserId: state.auth.user && state.auth.user.id,
  sessionUserName: state.auth.user && (state.auth.user.name || state.auth.user.login),
  sessionBotPromptIds: state.auth.user && state.auth.user.bot_prompt_ids,
  sessionBotPrompts: state.auth.user && state.auth.user.bot_prompts,
});
const mapDispatchToProps = (dispatch) => bindActionCreators({ getList, getListBets }, dispatch);
export default connect(mapStateToProps, mapDispatchToProps)(CrashLog);
